import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import SourcesTable from './SourcesTable';
import PageHeader from 'Utils/PageHeader';
import { getFetchHandler } from 'Secure/Utils';
import {
  getLatestDataSourceInventory,
  getSensitiveFieldCountFromInventory,
  inventoryContentOf,
  listEncryptJobs,
  listDecryptJobs,
  mapEncryptionRowStatus,
  mergeEncryptionJobsForDataSource,
  readableJobError,
  jobProgressFromJob,
} from 'Secure/api';
import FetchContainer from 'Utils/FetchContainer';
import EncryptContext from './EncryptContext';
import { usePolling } from 'Hooks';
import { BiLockAlt } from 'react-icons/bi';
import AppContext from 'Utils/AppContext';
import { getDataSourceNumericId, filterJobStartable } from 'DataSources/datasourcesApi';
import { compareByLatestJobDesc, jobTimestampMs } from 'Secure/jobSortOrder';
import { getHistory, getOverview } from 'DataDiscovery/discoverApi';
import { listDrivers } from 'Drivers/api';
import { hasMaskingDriverForEngine } from 'Drivers/maskingEngineMatch';
import { supportsFileMaskingForConnection } from 'Drivers/fileFormatSupport';
import { getServiceCapabilities, supportedFileFormatsForRole } from 'Secure/serviceCapabilitiesApi';

function EncryptionSources() {
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const { connections } = useContext(AppContext);
  const fetchData = useMemo(() => {
    try {
      return getFetchHandler(setFetchingData, setError, setData, getEncryptionSources);
    } catch (e) {
      console.log('Error fetching encryption sources', e);
      return () => {};
    }
  }, []);

  const fetchDataHandler = useCallback(
    async (from, sortKey, searchTerms) => {
      await fetchData(connections, from, sortKey, searchTerms);
    },
    [fetchData, connections]
  );

  const encryptContext = { fetchDataHandler };

  useEffect(() => {
    const init = async () => {
      await fetchDataHandler(0);
    };
    init();
  }, [fetchDataHandler]);

  const encryptPollDelayMs = useMemo(() => {
    const sources = data.sources || [];
    const anyRunning = sources.some(
      (s) => s && (s.status === 'RUNNING' || s.status === 'ENCRYPTING' || s.status === 'DECRYPTING')
    );
    return anyRunning ? 5000 : 30000;
  }, [data.sources]);

  usePolling(
    async () => {
      if (!fetchingData) {
        await fetchDataHandler();
      }
    },
    encryptPollDelayMs
  );

  return (
    <div>
      <FetchContainer error={error} fluid>
        <div>
          <PageHeader
            header='Encryption'
            icon={BiLockAlt}
            tooltip='Encrypt discovered data sources using format-preserving encryption. Use the gear icon to configure fields and keys; decrypt is only available after a successful encrypt job.'
          />
          <div>
            <EncryptContext.Provider value={encryptContext}>
              <SourcesTable data={data} fetchingData={fetchingData} fetchDataHandler={fetchDataHandler} />
            </EncryptContext.Provider>
          </div>
        </div>
      </FetchContainer>
    </div>
  );
}

async function getEncryptionSources(connections, _from, _sortKey, searchTerms) {
  // Imported-but-not-adopted rows (registeredServiceId present, managedBy !== 'c2')
  // don't have credentials — we can't encrypt/decrypt them. Exclude them up front.
  // mongodb stays excluded: encryption jobs don't support mongo (unlike masking, which is
  // mongodb→mongodb now). dynamodb is excluded too — encryption doesn't support it, and
  // masking rejects it as well (ENGINE_NOT_MASKABLE). Legacy s3/sftp `c.type` rows are let
  // through so file sources surface and have their capability checked per-format below.
  const filteredConnections = filterJobStartable(connections)
    .filter((c) => !['mongodb', 'dynamodb'].includes((c.type || '').toLowerCase()))
    .filter((c) =>
      searchTerms ? String(c.name || '').toLowerCase().includes(String(searchTerms).toLowerCase()) : true
    );

  const [encRes, decRes, drivers, capabilities] = await Promise.all([
    listEncryptJobs({ page: 0, size: 500, sort: ['id,desc'] }),
    listDecryptJobs({ page: 0, size: 500, sort: ['id,desc'] }),
    listDrivers().catch(() => []),
    getServiceCapabilities(),
  ]);
  const encryptFileFormats = supportedFileFormatsForRole(capabilities, 'encrypt');
  const encryptJobs = encRes && Array.isArray(encRes.content) ? encRes.content : [];
  const decryptJobs = decRes && Array.isArray(decRes.content) ? decRes.content : [];

  const enrichedSources = await Promise.all(
    filteredConnections.map(async (conn) => {
      const source = {
        connection_name: conn.name,
        engine: '',
        status: 'UNSECURED',
        time_stamp: null,
        prevSearchid: null,
        prevSearchName: '—',
        fieldQty: 0,
        errorMessage: null,
        dataSourceId: null,
        readOnly: conn.readOnly === true,
        tableData: conn.id != null ? { id: conn.id } : undefined,
      };
      const dataSourceId = getDataSourceNumericId(source);
      source.dataSourceId = dataSourceId;
      const meta =
        dataSourceId != null ? await getDiscoveryRowMetadata(dataSourceId) : { latestRun: null, discoverFieldCount: 0 };

      // A source can have several named inventories; the row summarizes the most recently
      // updated one (field count + PROVIDER column). The inventory page is where the user
      // picks a specific inventory.
      let fieldQty = 0;
      let latestInventory = null;
      try {
        if (dataSourceId != null) {
          latestInventory = await getLatestDataSourceInventory(dataSourceId);
          fieldQty = getSensitiveFieldCountFromInventory(inventoryContentOf(latestInventory));
          if (!fieldQty) fieldQty = meta.discoverFieldCount;
        }
      } catch (e) {
        if (dataSourceId != null) fieldQty = meta.discoverFieldCount;
      }

      const merged =
        dataSourceId != null ? mergeEncryptionJobsForDataSource(encryptJobs, decryptJobs, dataSourceId) : [];
      const status = mapEncryptionRowStatus(merged);
      const engine = displayEngineForEncryptRow(conn, meta.latestRun);
      const fileSupport = supportsFileMaskingForConnection(conn, encryptFileFormats);
      const hasMaskingDriver = fileSupport === null
        ? hasMaskingDriverForEngine(engine, drivers)
        : fileSupport;
      // Surface the most recent FAILED job's upstream error so it reaches the row's tooltip.
      // merged is newest-first; find the first FAILED job rather than gating on merged[0] being
      // failed (a later retry/non-failed job would otherwise hide a real failure). The job DTO
      // carries upstreamErrorBody (the worker's 404/5xx body) and errorCode (e.g. WORKER_REJECTED).
      const failedJob = merged.find((j) => j && j.status === 'FAILED');
      // Live progress comes from the currently-running job (newest PENDING/RUNNING). Results link
      // uses the newest job overall (merged[0]) — its jobId opens the encrypt/decrypt results page.
      const runningJob = merged.find((j) => j && (j.status === 'RUNNING' || j.status === 'PENDING'));
      const latestJob = merged[0] || null;
      return {
        ...source,
        engine,
        datasourceType: conn.datasourceType || null,
        preferredEncryptProviderId: latestInventory?.preferredEncryptProviderId || null,
        prevSearchid: meta.latestRun ? meta.latestRun.run_id : null,
        prevSearchName: formatDiscoveryLinkLabel(meta.latestRun),
        fieldQty,
        status,
        errorMessage: failedJob ? readableJobError(failedJob) : null,
        encryptJobProgress: runningJob ? jobProgressFromJob(runningJob) : null,
        latestJobId: latestJob ? latestJob.id : null,
        latestJobIsDecrypt: latestJob ? String(latestJob.jobType || '').toUpperCase().includes('DECRYPT') : false,
        latestJobAt: jobTimestampMs(latestJob),
        hasMaskingDriver,
      };
    })
  );

  // Most recently run job first, sources with no job last — a just-started job stays on page 1.
  const sources = enrichedSources.filter((s) => s.fieldQty > 0).sort(compareByLatestJobDesc);

  return {
    sources,
    totalConnections: sources.length,
  };
}

async function getDiscoveryRowMetadata(dataSourceId) {
  try {
    const history = await getHistory(0, 50, 'startedAt,desc', dataSourceId);
    const runs = history && history.runs ? history.runs : [];
    const latestRun = runs[0] || null;
    const latestSuccessful = runs.find((run) => run && run.status === 'COMPLETED');
    let discoverFieldCount = 0;
    if (latestSuccessful && latestSuccessful.run_id) {
      const overview = await getOverview(latestSuccessful.run_id, { useCache: false });
      const tables = overview && Array.isArray(overview.tables) ? overview.tables : [];
      tables.forEach((table) => {
        (table.fields || []).forEach((field) => {
          const findings = field && field.findings ? field.findings : {};
          if (Object.keys(findings).length > 0) {
            discoverFieldCount += 1;
          }
        });
      });
    }
    return { latestRun, discoverFieldCount };
  } catch (e) {
    return { latestRun: null, discoverFieldCount: 0 };
  }
}

function displayEngineForEncryptRow(conn, latestRun) {
  const t = (conn && conn.type ? String(conn.type) : '').trim();
  const lower = t.toLowerCase();
  if (lower && lower !== 'jdbc') return lower;
  const jobEngine = latestRun && latestRun.engine && String(latestRun.engine).trim();
  if (jobEngine) return jobEngine.toLowerCase();
  return lower;
}

function formatDiscoveryLinkLabel(latestRun) {
  if (!latestRun) return '—';
  const label = latestRun.label != null ? String(latestRun.label).trim() : '';
  if (label) return label;
  return latestRun.run_id ? String(latestRun.run_id) : '—';
}

export default EncryptionSources;
